import React, { useEffect, useMemo, useRef, useState } from "react";
import { Col, Container, Form, Modal, Row, Spinner, Tab, Table, Button } from "react-bootstrap";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import homeImage from "../../assets/images/home.svg";
import addicon from "../../assets/images/addicon.svg";
import editAction from "../../assets/images/editaction.svg";
import deleteAction from "../../assets/images/deleteaction.svg";
import bin from "../../assets/images/bin.png";
import Loader, { LoaderType } from "../common-component/Loader";
import Nodata from "../common-component/Nodata";
import { EKSNodeGroupService } from "../../services/EKSNodeGroupService";
import Select from "react-select";

const emptyForm = {
    subscribereksnodegroupid: "",
    nodegroup_name: "",
    subnets: [],
    min_size: 1,
    desired_size: 1,
    max_size: 1,
    instance_types: "m5.large",
    ami_type: "AL2_x86_64",
    capacity_type: "ON_DEMAND",
    disk_size: 20,
    labels: "",
    tag: "",
    status: "",
};

// const awsInstanceTypes = [];
// const AMITypeOptions = [];
const StepIndicator = ({ currentStep, totalSteps = 3 }) => {
    const steps = [
        { num: 1, label: "Compute" },
        { num: 2, label: "Configuration" },
        { num: 3, label: "Resources & Review" },
    ];

    return (
        <div className="create-db-step-indicator">
            {steps.map((step, index) => (
                <React.Fragment key={step.num}>
                    <div className={`step-item ${currentStep >= step.num ? "active" : ""} ${currentStep > step.num ? "completed" : ""}`}>
                        <div className="step-circle">
                            {currentStep > step.num ? (
                                <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                                    <path d="M13.78 4.22a.75.75 0 010 1.06l-7.25 7.25a.75.75 0 01-1.06 0L2.22 9.28a.75.75 0 011.06-1.06L6 10.94l6.72-6.72a.75.75 0 011.06 0z" />
                                </svg>
                            ) : (
                                step.num
                            )}
                        </div>
                        <span className="step-label">{step.label}</span>
                    </div>
                    {index < totalSteps - 1 && <div className={`step-line ${currentStep > step.num ? "completed" : ""}`} />}
                </React.Fragment>
            ))}
        </div>
    );
};

const storageSizes = [10, 20, 50, 100, 200, 500];
const POLLING_INTERVAL_MS = 10000;
const TERMINAL_STATUS_SET = new Set(["ACTIVE", "FAILED", "STOP"]);

const normalizeStatus = (status) => String(status ?? "").trim().toUpperCase();

const shouldPollStatus = (status) => !TERMINAL_STATUS_SET.has(normalizeStatus(status));

const getStatusColor = (status) => {
    switch (normalizeStatus(status)) {
        case "PENDING":
            return "#f59e0b";
        case "ACTIVE":
            return "#16a34a";
        case "FAILED":
            return "#dc2626";
        case "STOP":
            return "#dc2626";
        default:
            return "#6b7280";
    }
};

const getLatestNodeGroupStatus = (response) => {
    const payload = response?.data ?? response;
    const data = payload?.data ?? payload;
    return data?.status ?? data?.Status ?? payload?.status ?? payload?.Status ?? "";
};



function EKSNodeGroupListing() {
    const navigate = useNavigate();
    const location = useLocation();
    const { id: editId } = useParams();
    const isCreatePage = location.pathname === "/eks-node-group/create";
    const isEditPage = location.pathname === `/eks-node-group/edit/${editId}`;
    const editItemFromState = location.state?.item ?? null;
    const [loading, setLoading] = useState(true);
    const [nodeGroups, setNodeGroups] = useState([]);
    const [subnets, setSubnets] = useState([]);
    const [currentStep, setCurrentStep] = useState(1);
    const [instanceSearch, setInstanceSearch] = useState("");
    const [saveLoading, setSaveLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [formErrors, setFormErrors] = useState({});
    const [formData, setFormData] = useState(emptyForm);
    const [deleteItem, setDeleteItem] = useState(null);
    const [awsInstanceTypes, setAwsInstanceTypes] = useState([]);
    const [AMITypeOptions, setAMITypeOptions] = useState([]);
    const pollingTimerRef = useRef(null);
    const pollingInFlightRef = useRef(false);
    const mountedRef = useRef(true);
    const nodeGroupsRef = useRef([]);

    const stats = useMemo(() => {
        return {
            total: nodeGroups.length,
            onDemand: nodeGroups.filter((item) => String(item.capacity_type || "").toUpperCase() === "ON_DEMAND").length,
            spot: nodeGroups.filter((item) => String(item.capacity_type || "").toUpperCase() === "SPOT").length,
        };
    }, [nodeGroups]);

    const filteredInstanceTypes = useMemo(() => {
        const search = instanceSearch.trim().toLowerCase();
        return awsInstanceTypes.filter((item) => {
            const matchesSearch = !search || [item.type, item.family, item.processor, String(item.vcpu), String(item.memory)].some((value) =>
                String(value).toLowerCase().includes(search)
            );
            return matchesSearch;
        });
    }, [awsInstanceTypes, instanceSearch]);

    const selectedInstanceDetails = useMemo(() => {
        const selectedType = awsInstanceTypes.find((item) => item.type === formData.instance_types) || null;
        setFormData((prev) => ({
            ...prev,
            ami_type: selectedType?.ami_types?.length > 0 ? selectedType.ami_types : "AL2023_x86_64_STANDARD",
        }));

        return selectedType;
    }, [formData.instance_types]);

    const selectedNodeGroup = useMemo(() => {
        if (editItemFromState) {
            return editItemFromState;
        }

        if (!editId) {
            return null;
        }

        return nodeGroups.find((item) => String(item.subscribereksnodegroupid) === String(editId)) || null;
    }, [editId, editItemFromState, nodeGroups]);

    const clearPollingTimer = () => {
        if (pollingTimerRef.current) {
            clearInterval(pollingTimerRef.current);
            pollingTimerRef.current = null;
        }
    };

    const syncPollingTimer = (rows) => {
        const shouldPoll = rows.some((item) => shouldPollStatus(item.status));

        if (shouldPoll) {
            if (!pollingTimerRef.current) {
                pollingTimerRef.current = setInterval(() => {
                    void runStatusPollingCycle();
                }, POLLING_INTERVAL_MS);
            }
        } else {
            clearPollingTimer();
        }
    };

    const runStatusPollingCycle = async () => {
        if (pollingInFlightRef.current || !mountedRef.current) {
            return;
        }

        const pendingRows = nodeGroupsRef.current.filter((item) => shouldPollStatus(item.status) && item.nodegroup_name);
        if (pendingRows.length === 0) {
            clearPollingTimer();
            return;
        }

        pollingInFlightRef.current = true;

        try {
            const statusResults = await Promise.all(
                pendingRows.map(async (item) => {
                    try {
                        const response = await EKSNodeGroupService.checkStatusByInstanceName(item.nodegroup_name);
                        return {
                            name: item.nodegroup_name,
                            status: getLatestNodeGroupStatus(response),
                        };
                    } catch (error) {
                        return null;
                    }
                })
            );

            if (!mountedRef.current) {
                return;
            }

            const statusMap = new Map(
                statusResults
                    .filter((result) => result?.name && result.status)
                    .map((result) => [String(result.name), result.status])
            );

            if (statusMap.size === 0) {
                return;
            }

            setNodeGroups((prevRows) => {
                let hasChanges = false;
                const nextRows = prevRows.map((row) => {
                    const nextStatus = statusMap.get(String(row.nodegroup_name || ""));
                    if (!nextStatus) {
                        return row;
                    }

                    if (normalizeStatus(row.status) === normalizeStatus(nextStatus)) {
                        return row;
                    }

                    hasChanges = true;
                    return {
                        ...row,
                        status: nextStatus,
                    };
                });

                if (hasChanges && !nextRows.some((row) => shouldPollStatus(row.status))) {
                    clearPollingTimer();
                }

                return hasChanges ? nextRows : prevRows;
            });
        } finally {
            pollingInFlightRef.current = false;
        }
    };

    const fetchNodeGroups = async () => {
        setLoading(true);
        try {
            const response = await EKSNodeGroupService.getList();
            const payload = response?.data ?? response;
            const list = payload?.data ?? payload?.DataList1 ?? payload?.dataList1 ?? [];
            const items = Array.isArray(list) ? list : [];
            setNodeGroups(items);
            nodeGroupsRef.current = items;
            syncPollingTimer(items);
            return items;
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchNodeGroups().catch(() => {
            setLoading(false);
            toast.error("Unable to load EKS node groups.");
        });
    }, []);

    useEffect(() => {
        nodeGroupsRef.current = nodeGroups;
    }, [nodeGroups]);

    useEffect(() => {
        mountedRef.current = true;

        return () => {
            mountedRef.current = false;
            clearPollingTimer();
        };
    }, []);

    useEffect(() => {
        if (!isEditPage || !selectedNodeGroup) {
            return;
        }

        setFormErrors({});
        setFormData((prev) => ({
            ...prev,
            subscribereksnodegroupid: selectedNodeGroup.subscribereksnodegroupid || "",
            nodegroup_name: selectedNodeGroup.instancedisplayname || "",
            subnets: Array.isArray(selectedNodeGroup.subnets)
                ? selectedNodeGroup.subnets
                : String(selectedNodeGroup.subnets || "").split(",").map((item) => item.trim()).filter(Boolean),
            min_size: selectedNodeGroup.min_size ?? 1,
            desired_size: selectedNodeGroup.desired_size ?? 1,
            max_size: selectedNodeGroup.max_size ?? 1,
            instance_types: Array.isArray(selectedNodeGroup.instance_types)
                ? (selectedNodeGroup.instance_types[0] || "")
                : String(selectedNodeGroup.instance_types || ""),
            ami_type: selectedNodeGroup.ami_type || "",
            capacity_type: selectedNodeGroup.capacity_type || "",
            disk_size: selectedNodeGroup.disk_size ?? 20,
            labels: selectedNodeGroup.labels || "",
            tag: selectedNodeGroup.tag || "",
            status: selectedNodeGroup.status || "",
        }));
    }, [isEditPage, selectedNodeGroup]);

    useEffect(() => {

        const fetchSubnets = async () => {
            try {
                const response = await EKSNodeGroupService.getSubnet();
                const payload = response?.data ?? response;
                const subnet = payload?.data?.subnets ?? payload?.data?.SubnetList ?? payload?.subnets ?? [];
                console.log("EKS Node Group page mounted");

                if (Array.isArray(subnet)) {
                    setSubnets(subnet.length > 0 ? subnet : []);
                }
                else {
                    toast.error("Unable to load EKS subnets. Please Reload and try again.");
                }
                // const options = Array.isArray(subnets)
                //     ? subnets.map((item) => ({
                //         value: item.subnet_id,
                //         label: item.name ? `${item.subnet_id} (${item.name})` : item.subnet_id,
                //         availability_zone: item.availability_zone,
                //         cidr_block: item.cidr_block,
                //     }))
                //     : [];
                // setSubnetOptions(options);
            } catch (error) {
                toast.error("Unable to load EKS subnets.");
            } finally {
                // setSubnetLoading(false);
            }
        };

        fetchSubnets();
    }, []);

    useEffect(() => {
        const fetchAwsInstances = async () => {
            try {
                const response = await EKSNodeGroupService.getAwsInstances();
                const payload = response?.data.dataList1 ?? response;
                const obj = [];
                payload?.forEach((item) => {
                    obj.push({
                        type: item.instance_type,
                        family: item.instance_family,
                        processor: item.processer,
                        vcpu: item.cpu,
                        memory: item.memory,
                        ami_types: item.ami_types,
                    });
                });

                setAMITypeOptions([...new Set(payload.map(item => item.ami_types))]);

                setAwsInstanceTypes(obj);
                emptyForm.instance_types = obj.length > 0 ? obj[0].type : "";
            } catch (error) {
                toast.error("Unable to load AWS instances.");
            }
        };

        fetchAwsInstances();
    }, []);

    const openCreateModal = () => {
        setFormData(emptyForm);
        setFormErrors({});
        setCurrentStep(1);
        navigate("/eks-node-group/create");
    };

    const closeCreateModal = () => {
        setCurrentStep(1);
        setFormErrors({});
        setFormData(emptyForm);
        navigate("/eks-node-group");
    };

    const handleChange = (event) => {
        const { name, value } = event.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (formErrors[name]) {
            setFormErrors((prev) => ({ ...prev, [name]: null }));
        }
    };

    const handleInstanceSelect = (instanceType) => {
        setFormData((prev) => ({ ...prev, instance_types: instanceType }));
        if (formErrors.instance_types) {
            setFormErrors((prev) => ({ ...prev, instance_types: null }));
        }
    };

    const handleEditMinSizeChange = (event) => {
        const { value } = event.target;
        setFormData((prev) => ({ ...prev, min_size: value }));
        if (formErrors.min_size) {
            setFormErrors((prev) => ({ ...prev, min_size: null }));
        }
    };

    const handleEditStatusChange = (event) => {
        const { value } = event.target;
        setFormData((prev) => ({ ...prev, status: value }));
        if (formErrors.status) {
            setFormErrors((prev) => ({ ...prev, status: null }));
        }
    };

    const handleStorageSelect = (storageSize) => {
        setFormData((prev) => ({ ...prev, disk_size: storageSize }));
        if (formErrors.disk_size) {
            setFormErrors((prev) => ({ ...prev, disk_size: null }));
        }
    };

    const validateForm = () => {
        const errors = {};
        if (currentStep === 1) {
            if (!formData.nodegroup_name.trim()) errors.nodegroup_name = "Instance name is required.";
            else if (!/^[A-Za-z0-9_-]+$/.test(formData.nodegroup_name.trim())) {
                errors.nodegroup_name = "Instance name can contain only letters, numbers, underscore, and dash.";
            }
            if (!formData.instance_types.trim()) errors.instance_types = "Instance types are required.";
            if (!String(formData.min_size).trim()) errors.min_size = "Minimum size is required.";
        }

        if (currentStep === 2) {
            if (!formData.subnets.length) errors.subnets = "Subnets are required.";
            if (!String(formData.desired_size).trim()) errors.desired_size = "Desired size is required.";
            if (!String(formData.max_size).trim()) errors.max_size = "Maximum size is required.";
            if (!String(formData.disk_size).trim()) errors.disk_size = "Disk size is required.";
        }
        return errors;
    };

    const goToNextStep = async () => {
        setFormData((prev) => ({
                        ...prev,
                        subnets: subnets.length > 0 ? subnets.map((item) => item.subnet_id) : [],
                    }));
        const errors = validateForm();
        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }

        setFormErrors({});
        setCurrentStep((prev) => Math.min(prev + 1, 3));
    };

    const goToPrevStep = () => {
        setFormErrors({});
        setCurrentStep((prev) => Math.max(prev - 1, 1));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (currentStep !== 3) {
            await goToNextStep();
            return;
        }

        setSaveLoading(true);
        const payload = {
            ...formData,
            min_size: Number(formData.min_size),
            desired_size: Number(formData.min_size),
            max_size: Number(formData.min_size),
            disk_size: Number(formData.disk_size),
        };

        const response = await EKSNodeGroupService.save(payload);
        setSaveLoading(false);

        if (response?.status_code === 1 || response?.isValidate) {
            toast.success(response?.message || "EKS node group saved successfully.");
            closeCreateModal();
            fetchNodeGroups();
        } else {
            toast.error(response?.message || "Failed to save EKS node group.");
        }
    };

    const handleUpdateMinSize = async (event) => {
        event.preventDefault();

        if (!selectedNodeGroup?.subscribereksnodegroupid) {
            toast.error("Instance not found.");
            return;
        }

        if (!String(formData.min_size).trim()) {
            setFormErrors({ min_size: "Minimum size is required." });
            return;
        }

        setSaveLoading(true);
        const response = await EKSNodeGroupService.updateMinSize({
            subscribereksnodegroupid: selectedNodeGroup.subscribereksnodegroupid,
            min_size: Number(formData.min_size),
            nodegroupname: selectedNodeGroup.nodegroup_name,
            status: formData.status,
        });
        setSaveLoading(false);

        if (response?.status_code === 1 || response?.isValidate) {
            toast.success(response?.message || "Instance updated successfully.");
            navigate("/eks-node-group");
            fetchNodeGroups();
        } else {
            toast.error(response?.message || "Failed to update instance.");
        }
    };

    const handleDeleteClick = (item, event) => {
        event?.stopPropagation();
        setDeleteItem(item);
        setShowDeleteModal(true);
    };

    const handleConfirmDelete = async () => {
        if (!deleteItem?.subscribereksnodegroupid) return;
        const deleteobj = {
            subscribereksnodegroupid: deleteItem.subscribereksnodegroupid,
            nodegroupname: deleteItem.nodegroup_name
        };
        setDeleteLoading(true);
        const response = await EKSNodeGroupService.delete(deleteobj);
        setDeleteLoading(false);

        if (response?.status_code === 1 || response?.isValidate) {
            toast.success(response?.message || "EKS node group deleted successfully.");
            setShowDeleteModal(false);
            setDeleteItem(null);
            fetchNodeGroups();
        } else {
            toast.error(response?.message || "Failed to delete EKS node group.");
        }
    };

    const [showStatusModal, setShowStatusModal] = useState(false);
    const [selectedCompute, setSelectedCompute] = useState(null);
    const [statusUpdating, setStatusUpdating] = useState(false);

    const handleStatusToggle = (item) => {
        setSelectedCompute(item);
        setShowStatusModal(true);
    };

    const confirmStatusChange = async () => {
        if (!selectedCompute) return;

        const isActive =
            selectedCompute?.status?.toUpperCase() === "ACTIVE";

        try {
            setStatusUpdating(true);

            if (isActive) {
                const response = await EKSNodeGroupService.updateMinSize({
                    subscribereksnodegroupid: selectedCompute.subscribereksnodegroupid,
                    min_size: Number(selectedCompute.min_size),
                    nodegroupname: selectedCompute.nodegroup_name,
                    status: "Stop",
                });

                if (response?.status_code === 1 || response?.isValidate) {
                    toast.success(response?.message || "Compute status updated successfully.");
                    fetchNodeGroups();
                } else {
                    toast.error(response?.message || "Failed to update compute status.");
                }
            } else {
                const response = await EKSNodeGroupService.updateMinSize({
                    subscribereksnodegroupid: selectedCompute.subscribereksnodegroupid,
                    min_size: Number(selectedCompute.min_size),
                    nodegroupname: selectedCompute.nodegroup_name,
                    status: "Active",
                });
                if (response?.status_code === 1 || response?.isValidate) {
                    toast.success(response?.message || "Compute status updated successfully.");
                    fetchNodeGroups();
                } else {
                    toast.error(response?.message || "Failed to update compute status.");
                }
            }

            setShowStatusModal(false);
            setSelectedCompute(null);

        } catch (error) {
            console.error("Status update failed:", error);
        } finally {
            setStatusUpdating(false);
        }
    };

    if (isEditPage) {
        return (
            <div className="create-database-page database-page-main schedular-page-main all-page-top">
                <Container>
                    <div className="top-bot-section flex-column align-items-start gap-3">
                        <div className="d-flex justify-content-between align-items-center w-100">
                            <h6>
                                <img
                                    src={homeImage}
                                    alt="home"
                                    title="home"
                                    style={{ cursor: "pointer", marginBottom: "3px" }}
                                    onClick={() => navigate("/")}
                                />
                                <span style={{ fontWeight: "400", color: "#666666" }}> {" | "} </span>
                                <span
                                    onClick={() => navigate("/eks-node-group")}
                                    style={{ cursor: "pointer", color: "#666666" }}
                                >
                                    Compute
                                </span>
                                <span style={{ fontWeight: "400", color: "#666666" }}> {" | "} </span>
                                <span className="mt-1">Edit Instance</span>
                            </h6>
                        </div>
                    </div>
                </Container>

                <div className="main-page-content">
                    <Container>
                        <div className="create-db-form-container">

                            <Form onSubmit={handleUpdateMinSize}>
                                <div className="create-db-step-content">
                                    <div className="step-title">
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <ellipse cx="12" cy="5" rx="9" ry="3" />
                                            <path d="M3 5v7c0 1.66 4.03 3 9 3s9-1.34 9-3V5" />
                                            <path d="M3 12v7c0 1.66 4.03 3 9 3s9-1.34 9-3v-7" />
                                        </svg>
                                        Edit Compute Count
                                    </div>

                                    {!selectedNodeGroup ? (
                                        <div className="text-center py-4">
                                            <Spinner animation="border" size="sm" />
                                            <div className="mt-2 text-muted">Loading compute details...</div>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="create-db-summary eks-review-card">
                                                <div className="eks-review-grid" style={{ borderTop: "none" }}>
                                                    <div className="eks-review-item">
                                                        <div className="eks-review-icon">
                                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                <rect x="3" y="4" width="18" height="16" rx="2" ry="2" />
                                                                <path d="M7 8h10" />
                                                                <path d="M7 12h10" />
                                                            </svg>
                                                        </div>
                                                        <div>
                                                            <div className="eks-review-label">Compute Name</div>
                                                            <div className="eks-review-value">{formData.nodegroup_name || "-"}</div>
                                                        </div>
                                                    </div>

                                                    <div className="eks-review-item">
                                                        <div className="eks-review-icon">
                                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                <path d="M4 6h16v12H4z" />
                                                                <path d="M8 10h8" />
                                                                <path d="M8 14h5" />
                                                            </svg>
                                                        </div>
                                                        <div>
                                                            <div className="eks-review-label">Disk Size</div>
                                                            <div className="eks-review-value">{formData.disk_size || "-"} GB</div>
                                                        </div>
                                                    </div>

                                                    <div className="eks-review-item">
                                                        <div className="eks-review-icon">
                                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                <rect x="4" y="4" width="16" height="16" rx="2" ry="2" />
                                                                <rect x="9" y="9" width="6" height="6" />
                                                            </svg>
                                                        </div>
                                                        <div>
                                                            <div className="eks-review-label">Compute Type</div>
                                                            <div className="eks-review-value">{formData.instance_types || "-"}</div>
                                                        </div>
                                                    </div>

                                                    <div className="eks-review-item">
                                                        <div className="eks-review-icon">
                                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                <circle cx="12" cy="12" r="8" />
                                                                <path d="M12 8v4l3 2" />
                                                            </svg>
                                                        </div>
                                                        <div>
                                                            <div className="eks-review-label">Capacity Type</div>
                                                            <div className="eks-review-value">{formData.capacity_type || "-"}</div>
                                                        </div>
                                                    </div>

                                                    <div className="eks-review-item eks-review-item-full">
                                                        <div className="eks-review-icon">
                                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                <rect x="4" y="4" width="16" height="16" rx="2" ry="2" />
                                                                <path d="M12 8v8" />
                                                                <path d="M8 12h8" />
                                                            </svg>
                                                        </div>
                                                        <div>
                                                            <div className="eks-review-label">Current No of Compute</div>
                                                            <div className="eks-review-value">{selectedNodeGroup.min_size ?? "-"}</div>
                                                        </div>
                                                    </div>

                                                    <div className="eks-review-item eks-review-item-full">
                                                        <div className="eks-review-icon">
                                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                <path d="M20 7l-8-4-8 4 8 4 8-4z" />
                                                                <path d="M4 7v10l8 4 8-4V7" />
                                                            </svg>
                                                        </div>
                                                        <div>
                                                            <div className="eks-review-label">AMI Type</div>
                                                            <div className="eks-review-value">{formData.ami_type || "-"}</div>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="eks-review-subnets">
                                                    <div className="eks-review-icon">
                                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                            <circle cx="12" cy="5" r="2" />
                                                            <circle cx="5" cy="19" r="2" />
                                                            <circle cx="19" cy="19" r="2" />
                                                            <path d="M12 7v6" />
                                                            <path d="M12 13l-7 4" />
                                                            <path d="M12 13l7 4" />
                                                        </svg>
                                                    </div>
                                                    <div className="eks-subnet-content">
                                                        <div className="eks-review-label">Subnets</div>
                                                        {formData.subnets.length > 0 ? (
                                                            <div className="eks-subnet-list">
                                                                {formData.subnets.map((subnet) => (
                                                                    <span key={subnet} className="eks-subnet-item">{subnet}</span>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <div className="eks-review-value">-</div>
                                                        )}
                                                    </div>
                                                </div>
                                                <Row className="mt-3">
                                                    <Col md={6}>
                                                        <Form.Group className="mb-0">
                                                            <Form.Label className="common-label">No. of Compute *</Form.Label>
                                                            <Form.Control
                                                                type="number"
                                                                min="0"
                                                                name="min_size"
                                                                value={formData.min_size}
                                                                onChange={handleEditMinSizeChange}
                                                                isInvalid={!!formErrors.min_size}
                                                            />
                                                            <Form.Control.Feedback type="invalid">
                                                                {formErrors.min_size}
                                                            </Form.Control.Feedback>
                                                        </Form.Group>
                                                    </Col>
                                                    <Col md={6}>
                                                        <Form.Group className="mb-0">
                                                            <Form.Label className="common-label">Status</Form.Label>
                                                            <Form.Select
                                                                name="status"
                                                                value={formData.status}
                                                                onChange={handleEditStatusChange}
                                                                isInvalid={!!formErrors.status}
                                                            >
                                                                <option value="Active">Active</option>
                                                                <option value="Stop">Stop</option>
                                                            </Form.Select>
                                                            <Form.Control.Feedback type="invalid">
                                                                {formErrors.status}
                                                            </Form.Control.Feedback>
                                                        </Form.Group>
                                                    </Col>
                                                </Row>
                                            </div>

                                        </>
                                    )}

                                    <div className="step-actions">
                                        <button className="grey-btn" type="button" onClick={() => navigate("/eks-node-group")} disabled={saveLoading}>
                                            Cancel
                                        </button>
                                        <button className="blue-btn" type="submit" disabled={saveLoading || !selectedNodeGroup}>
                                            Update
                                            {saveLoading ? <Spinner animation="border" size="sm" className="ms-2" /> : null}
                                        </button>
                                    </div>
                                </div>
                            </Form>
                        </div>
                    </Container>
                </div>
            </div>
        );
    }

    if (isCreatePage) {
        return (
            <div className="create-database-page database-page-main schedular-page-main all-page-top">
                <Container>
                    <div className="top-bot-section flex-column align-items-start gap-3">
                        <div className="d-flex justify-content-between align-items-center w-100">
                            <h6>
                                <img
                                    src={homeImage}
                                    alt="home"
                                    title="home"
                                    style={{ cursor: "pointer", marginBottom: "3px" }}
                                    onClick={() => navigate("/")}
                                />
                                <span style={{ fontWeight: "400", color: "#666666" }}>{" | "}</span>
                                <span
                                    onClick={() => navigate("/eks-node-group")}
                                    style={{ cursor: "pointer", color: "#666666" }}
                                >
                                    Compute
                                </span>
                                <span style={{ fontWeight: "400", color: "#666666" }}>{" | "}</span>
                                <span className="mt-1">Add Instance</span>
                            </h6>
                        </div>
                    </div>
                </Container>

                <div className="main-page-content">
                    <Container>
                        <StepIndicator currentStep={currentStep} />

                        <div className="create-db-form-container">
                            <Form onSubmit={handleSubmit}>
                                {currentStep === 1 && (
                                    <div className="create-db-step-content">
                                        <div className="step-title">
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <ellipse cx="12" cy="5" rx="9" ry="3" />
                                                <path d="M3 5v7c0 1.66 4.03 3 9 3s9-1.34 9-3V5" />
                                                <path d="M3 12v7c0 1.66 4.03 3 9 3s9-1.34 9-3v-7" />
                                            </svg>
                                            Compute Details
                                        </div>

                                        <Row>
                                            <Col md={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label className="common-label">Compute Name *</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="nodegroup_name"
                                                        value={formData.nodegroup_name}
                                                        onChange={handleChange}
                                                        placeholder="e.g., eks-workers"
                                                        isInvalid={!!formErrors.nodegroup_name}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {formErrors.nodegroup_name}
                                                    </Form.Control.Feedback>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label className="common-label">No. of Compute *</Form.Label>
                                                    <Form.Control
                                                        type="number"
                                                        min="0"
                                                        name="min_size"
                                                        value={formData.min_size}
                                                        onChange={handleChange}
                                                        isInvalid={!!formErrors.min_size}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {formErrors.min_size}
                                                    </Form.Control.Feedback>
                                                </Form.Group>
                                            </Col>
                                        </Row>

                                        <div className="resource-selector-group mt-2">
                                            <div className="resource-selector-header">
                                                <div className="resource-selector-label">
                                                    <div className="resource-icon">
                                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                            <rect x="4" y="4" width="16" height="16" rx="2" ry="2" />
                                                            <rect x="9" y="9" width="6" height="6" />
                                                            <line x1="9" y1="1" x2="9" y2="4" />
                                                            <line x1="15" y1="1" x2="15" y2="4" />
                                                            <line x1="9" y1="20" x2="9" y2="23" />
                                                            <line x1="15" y1="20" x2="15" y2="23" />
                                                        </svg>
                                                    </div>
                                                    <div>
                                                        <span className="resource-name">Compute Type</span>
                                                        <span className="resource-desc">Search and choose the compute shape for this node group</span>
                                                    </div>
                                                </div>
                                                <div className="resource-selected-value">
                                                    {selectedInstanceDetails ? `${selectedInstanceDetails.type}` : "Select"}
                                                </div>
                                            </div>

                                            <Row className="mb-3">
                                                <Col md={12}>
                                                    <Form.Control
                                                        type="text"
                                                        className="common-input"
                                                        placeholder="Search by type, family, processor, vCPU, or memory"
                                                        value={instanceSearch}
                                                        onChange={(event) => setInstanceSearch(event.target.value)}
                                                    />
                                                </Col>
                                            </Row>

                                            <div className="db-type-grid eks-instance-grid">
                                                {filteredInstanceTypes.map((item) => (
                                                    <div
                                                        key={item.type}
                                                        className={`db-type-card ${formData.instance_types === item.type ? "selected" : ""}`}
                                                        onClick={() => handleInstanceSelect(item.type)}
                                                    >
                                                        <div className="db-type-icon">
                                                            <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                <rect x="3" y="4" width="18" height="16" rx="2" ry="2" />
                                                                <path d="M7 8h10" />
                                                                <path d="M7 12h10" />
                                                                <path d="M7 16h6" />
                                                            </svg>
                                                        </div>
                                                        <div className="db-type-name">{item.type}</div>
                                                        <div className="db-type-desc">{item.family} | {item.processor}</div>
                                                        <div className="db-type-desc mt-1">{item.vcpu} vCPU | {item.memory} GiB</div>
                                                        {formData.instance_types === item.type && (
                                                            <div className="db-type-check">
                                                                <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                                                                    <path d="M13.78 4.22a.75.75 0 010 1.06l-7.25 7.25a.75.75 0 01-1.06 0L2.22 9.28a.75.75 0 011.06-1.06L6 10.94l6.72-6.72a.75.75 0 011.06 0z" />
                                                                </svg>
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>

                                            {filteredInstanceTypes.length === 0 && (
                                                <div className="text-center py-4 text-muted">No instance types match your search.</div>
                                            )}

                                            {formErrors.instance_types && (
                                                <div className="text-danger mt-2" style={{ fontSize: "12px" }}>
                                                    {formErrors.instance_types}
                                                </div>
                                            )}
                                        </div>
                                        <div className="step-actions">
                                            <button className="grey-btn" type="button" onClick={closeCreateModal} disabled={saveLoading}>
                                                Cancel
                                            </button>
                                            <button className="blue-btn" type="button" onClick={goToNextStep} disabled={saveLoading}>
                                                Continue
                                                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" className="ms-2">
                                                    <path fillRule="evenodd" d="M4.646 1.646a.5.5 0 01.708 0l6 6a.5.5 0 010 .708l-6 6a.5.5 0 01-.708-.708L10.293 8 4.646 2.354a.5.5 0 010-.708z" />
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {currentStep === 2 && (
                                    <div className="create-db-step-content">
                                        <div className="step-title">
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <circle cx="12" cy="12" r="3" />
                                                <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z" />
                                            </svg>
                                            Compute Configuration
                                        </div>

                                        <Row>
                                            <Col md={12}>
                                                <div className="resource-selector-group">
                                                    <div className="resource-selector-header">
                                                        <div className="resource-selector-label">
                                                            <div className="resource-icon storage">
                                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                    <line x1="22" y1="12" x2="2" y2="12" />
                                                                    <path d="M5.45 5.11L2 12v6a2 2 0 002 2h16a2 2 0 002-2v-6l-3.45-6.89A2 2 0 0016.76 4H7.24a2 2 0 00-1.79 1.11z" />
                                                                    <line x1="6" y1="16" x2="6.01" y2="16" />
                                                                    <line x1="10" y1="16" x2="10.01" y2="16" />
                                                                </svg>
                                                            </div>
                                                            <div>
                                                                <span className="resource-name">Storage</span>
                                                                <span className="resource-desc">Allocate SSD storage for your instance</span>
                                                            </div>
                                                        </div>
                                                        <div className="resource-selected-value">{formData.disk_size} GB</div>
                                                    </div>

                                                    <div className="resource-options storage-options">
                                                        {storageSizes.map((size) => (
                                                            <div
                                                                key={size}
                                                                className={`resource-option ${formData.disk_size === size ? "selected" : ""}`}
                                                                onClick={() => handleStorageSelect(size)}
                                                            >
                                                                <span className="option-value">{size}</span>
                                                                <span className="option-unit">GB</span>
                                                            </div>
                                                        ))}
                                                    </div>

                                                    {formErrors.disk_size && (
                                                        <div className="text-danger mt-2" style={{ fontSize: "12px" }}>
                                                            {formErrors.disk_size}
                                                        </div>
                                                    )}
                                                </div>
                                            </Col>
                                        </Row>
                                        <div className="step-actions">
                                            <button className="grey-btn" type="button" onClick={goToPrevStep} disabled={saveLoading}>
                                                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" className="me-2">
                                                    <path fillRule="evenodd" d="M11.354 1.646a.5.5 0 010 .708L5.707 8l5.647 5.646a.5.5 0 01-.708.708l-6-6a.5.5 0 010-.708l6-6a.5.5 0 01.708 0z" />
                                                </svg>
                                                Back
                                            </button>
                                            <button className="blue-btn" type="button" onClick={goToNextStep} disabled={saveLoading}>
                                                Continue
                                                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" className="ms-2">
                                                    <path fillRule="evenodd" d="M4.646 1.646a.5.5 0 01.708 0l6 6a.5.5 0 010 .708l-6 6a.5.5 0 01-.708-.708L10.293 8 4.646 2.354a.5.5 0 010-.708z" />
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {currentStep === 3 && (
                                    <div className="create-db-step-content">
                                        <div className="eks-review-header">
                                            <div className="step-title eks-review-title">
                                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                                                    <line x1="8" y1="21" x2="16" y2="21" />
                                                    <line x1="12" y1="17" x2="12" y2="21" />
                                                </svg>
                                                Resources & Review
                                            </div>
                                            <p className="eks-review-subtitle">
                                                Review your instance details before provisioning.
                                            </p>
                                        </div>

                                        <div className="create-db-summary eks-review-card">
                                            <div className="summary-title">Summary</div>
                                            <p className="eks-summary-copy">
                                                Please review the details below. You can go back to make changes if needed.
                                            </p>

                                            <div className="eks-review-grid">
                                                <div className="eks-review-item">
                                                    <div className="eks-review-icon">
                                                        <svg
                                                            width="40"
                                                            height="40"
                                                            viewBox="0 0 40 40"
                                                            fill="none"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                        >
                                                            <rect
                                                                x="7"
                                                                y="6"
                                                                width="26"
                                                                height="11"
                                                                rx="2"
                                                                stroke="#1479C9"
                                                                strokeWidth="2"
                                                            />
                                                            <rect
                                                                x="7"
                                                                y="23"
                                                                width="26"
                                                                height="11"
                                                                rx="2"
                                                                stroke="#1479C9"
                                                                strokeWidth="2"
                                                            />

                                                            <circle cx="12" cy="11.5" r="1.5" fill="#1479C9" />
                                                            <circle cx="12" cy="28.5" r="1.5" fill="#1479C9" />

                                                            <path
                                                                d="M17 11.5H28"
                                                                stroke="#1479C9"
                                                                strokeWidth="2"
                                                                strokeLinecap="round"
                                                            />

                                                            <path
                                                                d="M17 28.5H28"
                                                                stroke="#1479C9"
                                                                strokeWidth="2"
                                                                strokeLinecap="round"
                                                            />
                                                        </svg>
                                                    </div>
                                                    <div>
                                                        <div className="eks-review-label">Compute Name</div>
                                                        <div className="eks-review-value">{formData.nodegroup_name || "-"}</div>
                                                    </div>
                                                </div>

                                                <div className="eks-review-item">
                                                    <div className="eks-review-icon">
                                                        <svg
                                                            width="40"
                                                            height="40"
                                                            viewBox="0 0 40 40"
                                                            fill="none"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                        >
                                                            <rect
                                                                x="8"
                                                                y="6"
                                                                width="24"
                                                                height="28"
                                                                rx="3"
                                                                stroke="#1479C9"
                                                                strokeWidth="2"
                                                            />

                                                            <path
                                                                d="M13 15H27"
                                                                stroke="#1479C9"
                                                                strokeWidth="2"
                                                                strokeLinecap="round"
                                                            />

                                                            <circle
                                                                cx="20"
                                                                cy="24"
                                                                r="5"
                                                                stroke="#1479C9"
                                                                strokeWidth="2"
                                                            />

                                                            <circle
                                                                cx="20"
                                                                cy="24"
                                                                r="1.5"
                                                                fill="#1479C9"
                                                            />
                                                        </svg>
                                                    </div>
                                                    <div>
                                                        <div className="eks-review-label">Disk Size</div>
                                                        <div className="eks-review-value">{formData.disk_size || "-"} GB</div>
                                                    </div>
                                                </div>

                                                <div className="eks-review-item">
                                                    <div className="eks-review-icon">
                                                        <svg
                                                            width="40"
                                                            height="40"
                                                            viewBox="0 0 40 40"
                                                            fill="none"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                        >
                                                            <rect
                                                                x="11"
                                                                y="11"
                                                                width="18"
                                                                height="18"
                                                                rx="2"
                                                                stroke="#1479C9"
                                                                strokeWidth="2"
                                                            />

                                                            <rect
                                                                x="16"
                                                                y="16"
                                                                width="8"
                                                                height="8"
                                                                rx="1"
                                                                stroke="#1479C9"
                                                                strokeWidth="2"
                                                            />

                                                            <path
                                                                d="M15 6V11"
                                                                stroke="#1479C9"
                                                                strokeWidth="2"
                                                                strokeLinecap="round"
                                                            />
                                                            <path
                                                                d="M20 6V11"
                                                                stroke="#1479C9"
                                                                strokeWidth="2"
                                                                strokeLinecap="round"
                                                            />
                                                            <path
                                                                d="M25 6V11"
                                                                stroke="#1479C9"
                                                                strokeWidth="2"
                                                                strokeLinecap="round"
                                                            />

                                                            <path
                                                                d="M15 29V34"
                                                                stroke="#1479C9"
                                                                strokeWidth="2"
                                                                strokeLinecap="round"
                                                            />
                                                            <path
                                                                d="M20 29V34"
                                                                stroke="#1479C9"
                                                                strokeWidth="2"
                                                                strokeLinecap="round"
                                                            />
                                                            <path
                                                                d="M25 29V34"
                                                                stroke="#1479C9"
                                                                strokeWidth="2"
                                                                strokeLinecap="round"
                                                            />

                                                            <path
                                                                d="M6 15H11"
                                                                stroke="#1479C9"
                                                                strokeWidth="2"
                                                                strokeLinecap="round"
                                                            />
                                                            <path
                                                                d="M6 20H11"
                                                                stroke="#1479C9"
                                                                strokeWidth="2"
                                                                strokeLinecap="round"
                                                            />
                                                            <path
                                                                d="M6 25H11"
                                                                stroke="#1479C9"
                                                                strokeWidth="2"
                                                                strokeLinecap="round"
                                                            />

                                                            <path
                                                                d="M29 15H34"
                                                                stroke="#1479C9"
                                                                strokeWidth="2"
                                                                strokeLinecap="round"
                                                            />
                                                            <path
                                                                d="M29 20H34"
                                                                stroke="#1479C9"
                                                                strokeWidth="2"
                                                                strokeLinecap="round"
                                                            />
                                                            <path
                                                                d="M29 25H34"
                                                                stroke="#1479C9"
                                                                strokeWidth="2"
                                                                strokeLinecap="round"
                                                            />
                                                        </svg>
                                                    </div>
                                                    <div>
                                                        <div className="eks-review-label">Compute Type</div>
                                                        <div className="eks-review-value">{formData.instance_types || "-"}</div>
                                                    </div>
                                                </div>

                                                <div className="eks-review-item">
                                                    <div className="eks-review-icon">
                                                        <svg
                                                            width="40"
                                                            height="40"
                                                            viewBox="0 0 40 40"
                                                            fill="none"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                        >
                                                            <path
                                                                d="M7 27C7 18.716 12.82 12 20 12C27.18 12 33 18.716 33 27"
                                                                stroke="#1479C9"
                                                                strokeWidth="2"
                                                                strokeLinecap="round"
                                                            />

                                                            <path
                                                                d="M12 27H28"
                                                                stroke="#1479C9"
                                                                strokeWidth="2"
                                                                strokeLinecap="round"
                                                            />

                                                            <path
                                                                d="M20 20L25 17"
                                                                stroke="#1479C9"
                                                                strokeWidth="2"
                                                                strokeLinecap="round"
                                                            />

                                                            <circle
                                                                cx="20"
                                                                cy="20"
                                                                r="2"
                                                                fill="#1479C9"
                                                            />
                                                        </svg>
                                                    </div>
                                                    <div>
                                                        <div className="eks-review-label">Capacity Type</div>
                                                        <div className="eks-review-value">{formData.capacity_type || "-"}</div>
                                                    </div>
                                                </div>

                                                <div className="eks-review-item eks-review-item-full">
                                                    <div className="eks-review-icon">
                                                        <svg
                                                            width="40"
                                                            height="40"
                                                            viewBox="0 0 40 40"
                                                            fill="none"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                        >
                                                            <rect
                                                                x="8"
                                                                y="5"
                                                                width="24"
                                                                height="30"
                                                                rx="3"
                                                                stroke="#1479C9"
                                                                strokeWidth="2"
                                                            />

                                                            <path
                                                                d="M15 13H25"
                                                                stroke="#1479C9"
                                                                strokeWidth="2"
                                                                strokeLinecap="round"
                                                            />

                                                            <path
                                                                d="M17 10V16"
                                                                stroke="#1479C9"
                                                                strokeWidth="2"
                                                                strokeLinecap="round"
                                                            />

                                                            <path
                                                                d="M23 10V16"
                                                                stroke="#1479C9"
                                                                strokeWidth="2"
                                                                strokeLinecap="round"
                                                            />

                                                            <path
                                                                d="M15 22H25"
                                                                stroke="#1479C9"
                                                                strokeWidth="2"
                                                                strokeLinecap="round"
                                                            />

                                                            <path
                                                                d="M20 19V29"
                                                                stroke="#1479C9"
                                                                strokeWidth="2"
                                                                strokeLinecap="round"
                                                            />
                                                        </svg>
                                                    </div>
                                                    <div>
                                                        <div className="eks-review-label">No of Compute</div>
                                                        <div className="eks-review-value">{formData.min_size || "-"}</div>
                                                    </div>
                                                </div>

                                                <div className="eks-review-item eks-review-item-full">
                                                    <div className="eks-review-subnets">
                                                        <div className="eks-review-icon">
                                                            <svg
                                                                width="40"
                                                                height="40"
                                                                viewBox="0 0 40 40"
                                                                fill="none"
                                                                xmlns="http://www.w3.org/2000/svg"
                                                            >
                                                                <rect
                                                                    x="17"
                                                                    y="5"
                                                                    width="6"
                                                                    height="6"
                                                                    rx="1"
                                                                    stroke="#1479C9"
                                                                    strokeWidth="2"
                                                                />

                                                                <rect
                                                                    x="7"
                                                                    y="28"
                                                                    width="6"
                                                                    height="6"
                                                                    rx="1"
                                                                    stroke="#1479C9"
                                                                    strokeWidth="2"
                                                                />

                                                                <rect
                                                                    x="27"
                                                                    y="28"
                                                                    width="6"
                                                                    height="6"
                                                                    rx="1"
                                                                    stroke="#1479C9"
                                                                    strokeWidth="2"
                                                                />

                                                                <path
                                                                    d="M20 11V22"
                                                                    stroke="#1479C9"
                                                                    strokeWidth="2"
                                                                />

                                                                <path
                                                                    d="M10 28V23H30V28"
                                                                    stroke="#1479C9"
                                                                    strokeWidth="2"
                                                                    strokeLinejoin="round"
                                                                />
                                                            </svg>
                                                        </div>
                                                        <div className="eks-subnet-content">
                                                            <div className="eks-review-label">Subnets</div>
                                                            {formData.subnets.length > 0 ? (
                                                                <div className="eks-subnet-list">
                                                                    {formData.subnets.map((subnet) => (
                                                                        <span key={subnet} className="eks-subnet-item">{subnet}</span>
                                                                    ))}
                                                                </div>
                                                            ) : (
                                                                <div className="eks-review-value">-</div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="eks-review-item eks-review-item-full">
                                                    <div className="eks-review-icon">
                                                        <svg
                                                            width="40"
                                                            height="40"
                                                            viewBox="0 0 40 40"
                                                            fill="none"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                        >
                                                            <path
                                                                d="M7 19V9C7 7.89543 7.89543 7 9 7H19L33 21L21 33L7 19Z"
                                                                stroke="#1479C9"
                                                                strokeWidth="2"
                                                                strokeLinejoin="round"
                                                            />

                                                            <circle
                                                                cx="13"
                                                                cy="13"
                                                                r="2"
                                                                stroke="#1479C9"
                                                                strokeWidth="2"
                                                            />
                                                        </svg>
                                                    </div>
                                                    <div>
                                                        <div className="eks-review-label">AMI Type</div>
                                                        <div className="eks-review-value">{formData.ami_type || "-"}</div>
                                                    </div>
                                                </div>
                                            </div>


                                        </div>

                                        <div className="eks-review-info">
                                            <svg
                                                width="32"
                                                height="32"
                                                viewBox="0 0 32 32"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <circle
                                                    cx="16"
                                                    cy="16"
                                                    r="14"
                                                    fill="#1479C9"
                                                />

                                                <path
                                                    d="M16 14V23"
                                                    stroke="white"
                                                    strokeWidth="2.5"
                                                    strokeLinecap="round"
                                                />

                                                <circle
                                                    cx="16"
                                                    cy="9.5"
                                                    r="1.5"
                                                    fill="white"
                                                />
                                            </svg>
                                            <div>
                                                <strong>What happens next?</strong>
                                                <p>
                                                    After you click Save, your instance will be created with the above configuration. You can monitor its progress in the Instances dashboard.
                                                </p>
                                            </div>
                                        </div>

                                        <div className="step-actions">
                                            <button className="grey-btn" type="button" onClick={goToPrevStep} disabled={saveLoading}>
                                                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" className="me-2">
                                                    <path fillRule="evenodd" d="M11.354 1.646a.5.5 0 010 .708L5.707 8l5.647 5.646a.5.5 0 01-.708.708l-6-6a.5.5 0 010-.708l6-6a.5.5 0 01.708 0z" />
                                                </svg>
                                                Back
                                            </button>
                                            <button className="blue-btn" type="button" onClick={handleSubmit} disabled={saveLoading}>
                                                Save
                                                {saveLoading ? <Spinner animation="border" size="sm" className="ms-2" /> : null}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </Form>
                        </div>
                    </Container>
                </div>
            </div>
        );
    }

    return (
        <div className="database-page-main schedular-page-main all-page-top">
            <Container>
                <div className="top-bot-section flex-column align-items-start gap-3">
                    <div className="d-flex justify-content-between align-items-center w-100">
                        <h6>
                            <img
                                src={homeImage}
                                alt="home"
                                title="home"
                                style={{ cursor: "pointer", marginBottom: "3px" }}
                                onClick={() => navigate("/")}
                            />
                            <span style={{ fontWeight: "400", color: "#666666" }}> {" | "} </span>
                            <span className="mt-1">Compute</span>
                        </h6>

                        <div className="d-flex gap-2 align-items-center">
                            <div className="filtering-main" onClick={fetchNodeGroups} style={{ cursor: "pointer" }}>
                                <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" className={loading ? "spin-animation" : ""}>
                                    <path d="M13.65 2.35A7.958 7.958 0 008 0C3.58 0 .01 3.58.01 8S3.58 16 8 16c3.73 0 6.84-2.55 7.73-6h-2.08A5.99 5.99 0 018 14 6 6 0 012 8a6 6 0 016-6c1.66 0 3.14.69 4.22 1.78L9 7h7V0l-2.35 2.35z" />
                                </svg>
                                <span>Refresh</span>
                            </div>

                            <div className="filtering-main" onClick={openCreateModal} style={{ cursor: "pointer" }}>
                                <img src={addicon} alt="addicon" />
                                <span>Add Compute</span>
                            </div>
                        </div>
                    </div>

                    <div className="my-machine-top-boxes-wrapper">
                        <div className="my-machine-top-box-single total-machines in-process-machines scheduler-status-box">
                            <div><h4>{stats.total}</h4></div>
                            <p>Total Compute</p>
                        </div>
                        <div className="my-machine-top-box-single pending-machines approved-machines scheduler-status-box">
                            <div><h4>{stats.onDemand}</h4></div>
                            <p>On Demand</p>
                        </div>
                        <div className="my-machine-top-box-single pending-machines rejected-machines scheduler-status-box">
                            <div><h4>{stats.spot}</h4></div>
                            <p>Spot</p>
                        </div>
                    </div>
                </div>
            </Container>

            <div className="main-page-content">
                <Container>
                    <div className="common-table-main">
                        <div className="table-wrapper-main">
                            <Table responsive>
                                <thead>
                                    <tr>
                                        <th className="width150 sticky-workflow text-nowrap">Compute Name</th>
                                        {/* <th className="text-center width120">Subscriber</th> */}
                                        <th className="text-center width150">Compute Type</th>
                                        <th className="text-center width100">Storage</th>
                                        {/* <th className="text-center width150">AMI Type</th> */}
                                        <th className="text-center width150">No of Compute</th>
                                        <th className="text-center width150">Status</th>
                                        <th className="text-center width150">Capacity</th>
                                        {/* <th className="text-center width150">Tag</th> */}
                                        <th className="text-end width100 sticky-actions">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <Loader type={LoaderType.TableRow} count={5} nooftd={10} />
                                    ) : nodeGroups.length > 0 ? (
                                        nodeGroups.map((item) => (
                                            <tr key={item.subscribereksnodegroupid}>
                                                <td className="theme-color-text fw600 sticky-workflow text-nowrap">
                                                    <span onClick={() => navigate(`/eks-node-group/edit/${item.subscribereksnodegroupid}`, { state: { item } })}
                                                        style={{ cursor: "pointer" }}>{item.instancedisplayname || "-"}</span>
                                                </td>
                                                {/* <td className="text-center">{item.subscriberid || "-"}</td> */}
                                                <td className="text-center text-nowrap">{item.instance_types || "-"}</td>
                                                <td className="text-center">{item.disk_size || "-"}</td>
                                                {/* <td className="text-center text-nowrap">{item.ami_type || "-"}</td> */}
                                                <td className="text-center text-nowrap">
                                                    {item.min_size ?? "-"}
                                                </td>
                                                <td className="text-center text-nowrap">
                                                    {/* <span style={{ color: getStatusColor(item.status), fontWeight: 600 }}>
                                                        {item.status || "-"}
                                                    </span> */}
                                                    {item?.status?.toUpperCase() === "UPDATING"
                                                        ? (
                                                            <div className="compute-status-updating">
                                                                <Spinner
                                                                    animation="border"
                                                                    size="sm"
                                                                    className="me-2"
                                                                />
                                                                <span>Updating...</span>
                                                            </div>
                                                        )
                                                        : (<div className="compute-toggle-wrapper">
                                                            <input
                                                                type="checkbox"
                                                                className="compute-toggle"
                                                                id={`compute-toggle-${item?.subscribereksnodegroupid}`}
                                                                checked={item?.status?.toUpperCase() === "ACTIVE"}
                                                                onChange={() => handleStatusToggle(item)}
                                                            />

                                                            <label
                                                                htmlFor={`compute-toggle-${item?.subscribereksnodegroupid}`}
                                                                className="compute-toggle-label"
                                                            >
                                                                <span className="compute-toggle-slider"></span>
                                                            </label>

                                                            <span
                                                                className={
                                                                    item?.status?.toUpperCase() === "ACTIVE"
                                                                        ? "compute-status-active"
                                                                        : "compute-status-stopped"
                                                                }
                                                            >
                                                                {item?.status?.toUpperCase() === "ACTIVE"
                                                                    ? "Active"
                                                                    : "Stop"}
                                                            </span>
                                                        </div>
                                                        )}

                                                </td>
                                                <td className="text-center text-nowrap">{item.capacity_type || "-"}</td>
                                                {/* <td className="text-center text-nowrap">{item.tag || "-"}</td> */}
                                                <td className="sticky-actions" onClick={(event) => event.stopPropagation()}>
                                                    <div className="all-actions Scheduler-page justify-content-end">
                                                        <img
                                                            src={editAction}
                                                            alt="editAction"
                                                            title="Edit Node Group"
                                                            onClick={() => navigate(`/eks-node-group/edit/${item.subscribereksnodegroupid}`, { state: { item } })}
                                                            style={{ cursor: "pointer" }}
                                                        />
                                                        <img
                                                            src={deleteAction}
                                                            alt="deleteAction"
                                                            title="Delete Node Group"
                                                            onClick={(event) => handleDeleteClick(item, event)}
                                                            style={{ cursor: "pointer" }}
                                                        />
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={10}>
                                                <Nodata />
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </div>
                    </div>
                </Container>
            </div>

            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} size="md" centered className="delete-modal" backdrop="static">
                <Modal.Body>
                    <div className="text-center">
                        <img src={bin} alt="bin" className="modal-delete-icon" />
                        <h4>Delete Compute?</h4>
                        <p>
                            Are you sure you want to delete <strong>"{deleteItem?.instancedisplayname || "this compute"}"</strong>?
                            <br />This action cannot be undone.
                        </p>
                        <div className="d-flex align-items-center gap-2 justify-content-center">
                            <button className="grey-btn" onClick={() => setShowDeleteModal(false)} disabled={deleteLoading}>No, Keep</button>
                            <button className="red-btn" onClick={handleConfirmDelete} disabled={deleteLoading}>
                                {deleteLoading ? <Spinner animation="border" size="sm" /> : "Yes, Delete"}
                            </button>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>
            <Modal show={showStatusModal} onHide={() => {
                if (!statusUpdating) {
                    setShowStatusModal(false);
                    setSelectedCompute(null);
                }
            }} size="md" centered className="delete-modal" backdrop="static">
                <Modal.Body>
                    <div className="text-center">
                        {/* <img src={bin} alt="bin" className="modal-delete-icon" /> */}
                        <h4>Compute Status Change?</h4>
                        <p>
                            Are you sure you want to{" "}
                            <strong>
                                {selectedCompute?.status?.toUpperCase() === "ACTIVE"
                                    ? "Stop"
                                    : "Start"}
                            </strong>{" "}
                            {" "}<strong>"{selectedCompute?.instancedisplayname}"</strong> {" "}
                            this compute?
                        </p>
                        <div className="d-flex align-items-center gap-2 justify-content-center">
                            <button
                                className="grey-btn"
                                onClick={() => {
                                    setShowStatusModal(false);
                                    setSelectedCompute(null);
                                }}
                                disabled={statusUpdating}
                            >
                                No, Keep
                            </button>

                            <button
                                className="red-btn"
                                onClick={confirmStatusChange}
                                disabled={statusUpdating}
                            >
                                {statusUpdating ? (
                                    <Spinner animation="border" size="sm" />
                                ) : (
                                    "Yes, Confirm"
                                )}
                            </button>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>

        </div>
    );
}

export default EKSNodeGroupListing;